
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {CommissionProfileGetResponse} from './CommissionProfileGetResponse';

/**
 * CommissionProfileGetRequest struct
 */
class CommissionProfileGetRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {UUID=} commissionProfileId
   * @param {string=} commissionProfileName
   * @constructor
   */
  constructor (parent = new ConfigRequest(), commissionProfileId = null, commissionProfileName = null) {
    super()
    super.copy(parent)
    this.CommissionProfileId = commissionProfileId
    this.CommissionProfileName = commissionProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionProfileGetRequest}
   * @param {!CommissionProfileGetRequest} other Other struct
   * @returns {!CommissionProfileGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CommissionProfileId != null) {
      this.CommissionProfileId = new UUID(other.CommissionProfileId)
    } else {
      this.CommissionProfileId = null
    }
    if (other.CommissionProfileName != null) {
      this.CommissionProfileName = other.CommissionProfileName
    } else {
      this.CommissionProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionProfileGetRequest}
   * @returns {!CommissionProfileGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionProfileGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionProfileGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionProfileGetRequest}
   * @param {!CommissionProfileGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionProfileGetRequest)) {
      throw new TypeError('Instance of CommissionProfileGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionProfileGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CommissionProfileId: ((this.CommissionProfileId != null) ? this.CommissionProfileId.toString() : null),
      CommissionProfileName: ((this.CommissionProfileName != null) ? this.CommissionProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionProfileGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionProfileGetRequest} other Object value
   * @returns {!CommissionProfileGetRequest} Created struct
   */
  static fromObject (other) {
    return new CommissionProfileGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionProfileGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionProfileGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionProfileGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6122
  }
}

export { CommissionProfileGetRequest };

/**
 * Fast Binary Encoding CommissionProfileGetRequest field model
 */
class FieldModelCommissionProfileGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.CommissionProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.CommissionProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.CommissionProfileId.fbeOffset + this.CommissionProfileId.fbeSize), buffer, this.CommissionProfileId.fbeOffset + this.CommissionProfileId.fbeSize)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CommissionProfileId.fbeSize + this.CommissionProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CommissionProfileId.fbeExtra + this.CommissionProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionProfileGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6122
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileId.fbeSize

    if ((fbeCurrentSize + this.CommissionProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!CommissionProfileGetRequest} fbeValue Default value, defaults is new CommissionProfileGetRequest()
   * @returns {!CommissionProfileGetRequest} CommissionProfileGetRequest value
   */
  get (fbeValue = new CommissionProfileGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!CommissionProfileGetRequest} fbeValue CommissionProfileGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfileId = this.CommissionProfileId.get()
    } else {
      fbeValue.CommissionProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileId.fbeSize

    if ((fbeCurrentSize + this.CommissionProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfileName = this.CommissionProfileName.get()
    } else {
      fbeValue.CommissionProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {!CommissionProfileGetRequest} fbeValue CommissionProfileGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionProfileGetRequest}
   * @param {CommissionProfileGetRequest} fbeValue CommissionProfileGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CommissionProfileId.set(fbeValue.CommissionProfileId)
    this.CommissionProfileName.set(fbeValue.CommissionProfileName)
  }
}

export { FieldModelCommissionProfileGetRequest };

/**
 * Fast Binary Encoding CommissionProfileGetRequest model
 */
class CommissionProfileGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionProfileGetRequest(this.buffer, 4)
  }

  /**
   * Get the CommissionProfileGetRequest model
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!FieldModelCommissionProfileGetRequest} model CommissionProfileGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionProfileGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionProfileGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionProfileGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionProfileGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionProfileGetRequestModel}
   * @param {!CommissionProfileGetRequest} value CommissionProfileGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionProfileGetRequestModel}
   * @param {!CommissionProfileGetRequest} value CommissionProfileGetRequest value, defaults is new CommissionProfileGetRequest()
   * @return {!object} Deserialized CommissionProfileGetRequest value and its size
   */
  deserialize (value = new CommissionProfileGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionProfileGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionProfileGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionProfileGetRequestModel}
   * @param {!number} prev Previous CommissionProfileGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionProfileGetRequestModel };
CommissionProfileGetRequest.__has_response = true;
CommissionProfileGetRequest.__response_class = CommissionProfileGetResponse;
